<template>
    <main class="min-h-screen w-full">
        <WYSIWYGEditorToolbar
            v-if="activeComponents.editor.active"
            :key="activeComponents.content.colId"
            :active="activeComponents.editor.active"
            :editor="activeComponents.editor.instance"
        />

        <div
            class="active-edit:right-96 fixed top-36 right-0 bottom-0 left-0 z-19 overflow-x-auto overflow-y-auto pb-24 text-left md:top-44 lg:left-64"
        >
            <div class="template-base-wrapper">
                <div
                    :class="[
                        dragging === true ? 'outline-sky-700' : 'focus:outline-hidden',
                        `template-base template-base-email relative w-full min-w-fit ${
                            activeComponents.showBlocks === true ? 'template-show-blocks' : ''
                        }`,
                    ]"
                >
                    <!-- Row wrapper -->
                    <Sortable
                        :list="templateEditModel.template.body"
                        item-key="id"
                        class="row-wrapper w-full"
                        :options="options"
                        @update="(event) => showSortedDocument(templateEditModel, event)"
                    >
                        <template #item="{ element }">
                            <div
                                class="list-group-item group/rows my-2 w-full p-1 outline-2 outline-solid"
                                :class="
                                    activeComponents.content.rowId === element.id
                                        ? 'outline-amber-400 hover:outline-amber-400'
                                        : 'outline-transparent hover:outline-amber-200'
                                "
                            >
                                <div
                                    v-if="element.extra.type === 'iterator'"
                                    class="mr-1 ml-8 rounded-sm bg-emerald-200 px-2 py-1 text-right text-sm font-medium text-gray-800"
                                >
                                    <MdiRepeat class="mr-1 inline h-4 w-4" />
                                    <span class="select-none"
                                        >{{ t('placeholderTypeMultiple') }}:
                                    </span>
                                    <b>{{ element.extra.placeholder }}</b>
                                </div>
                                <div class="flex">
                                    <!-- Toolbar -->
                                    <div class="h-fit w-8 group-hover/rows:block">
                                        <div class="bg-white">
                                            <!-- Drag handle -->
                                            <div class="handle my-px flex cursor-pointer p-1">
                                                <MdiMouseMoveVertical
                                                    :class="[
                                                        activeComponents.showBlocks === true
                                                            ? 'hover:text-gray-600'
                                                            : 'opacity-25 hover:text-gray-400',
                                                        'h-6 w-6 text-gray-400',
                                                    ]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex-1">
                                        <!-- Col warpper -->
                                        <div
                                            :id="element.id"
                                            :class="element.cssClass"
                                            :style="extractStyles(element.style)"
                                            :data-extra-type="element.extra.type"
                                            :data-extra-placeholder="`${t('placeholderTypeMultiple')}: ${element.extra.placeholder}`"
                                        >
                                            <div
                                                v-for="col in element.cols"
                                                :id="col.id"
                                                :key="col.id"
                                                :class="[
                                                    activeComponents.content.colId === col.id
                                                        ? 'selected-col'
                                                        : '',
                                                    `${col.cssClass} p-1 pb-2`,
                                                ]"
                                                :style="extractStyles(col.style)"
                                            >
                                                <!-- Col content wrapper -->
                                                <div
                                                    :id="col.content.id"
                                                    :class="[
                                                        activeComponents.showBlocks === true
                                                            ? 'template-show-blocks'
                                                            : '',
                                                        col.content.cssClass,
                                                    ]"
                                                    :style="extractStyles(col.content.style)"
                                                    @mousedown="
                                                        setActiveContent(
                                                            activeComponents,
                                                            col.content,
                                                        )
                                                    "
                                                >
                                                    <WYSIWYGEditorCore
                                                        :model-value="col.content.html"
                                                        @update-content:model-value="
                                                            updateColContent
                                                        "
                                                        @focus-content:model-value="focusColContent"
                                                        @blur-content:model-value="blurColContent"
                                                    />
                                                </div>
                                                <span class="clear-both"></span>
                                            </div>
                                            <span class="clear-both"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Sortable>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { ref, onMounted, nextTick, provide } from 'vue';
import {
    updateColContent,
    focusColContent,
    setActiveContent,
    showSortedDocument,
    blurColContent,
    sizeInfoHandler,
} from '../composables/useWYSIWYGEditorUtils';
import { extractStyles } from '../composables/useTemplateModel';
import { MdiMouseMoveVertical } from 'materialdesignicons-vue3/icons/';
import WYSIWYGEditorCore from '../components/WYSIWYGEditorCore.vue';
import WYSIWYGEditorToolbar from '../components/WYSIWYGEditorToolbar.vue';
import { Sortable } from 'sortablejs-vue3';
import useWYSIWYGEditorExtensions from '../composables/useWYSIWYGEditorExtensions';
import { MdiRepeat } from 'materialdesignicons-vue3/icons/';

const props = defineProps({
    templateEditModel: {
        type: Object,
        required: true,
    },
    activeComponents: {
        type: Object,
        required: true,
    },
});

const activeComponents = props.activeComponents;
const templateEditModel = props.templateEditModel;
const dragging = ref(false);

provide('useWYSIWYGEditorExtensions', useWYSIWYGEditorExtensions());

const options = {
    handle: '.handle',
    ghostClass: 'ghost',
    chosenClass: 'chosen',
    dragClass: 'dragging',
    animation: '200',
    dataIdAttr: 'id',
    onStart: () => {
        dragging.value = true;
    },
    onEnd: () => {
        dragging.value = false;
    },
};

const visualDOMhelper = () => {
    //'.template-col-content table tbody th, .template-col-content table tbody td',
    const cells = document.querySelectorAll('.template-base');

    cells.forEach((cell) => {
        cell.addEventListener('mousemove', sizeInfoHandler.on);
        cell.addEventListener('mouseout', sizeInfoHandler.off);
    });
};

onMounted(async () => {
    await nextTick();
    visualDOMhelper();
});
</script>
<style>
#size-info {
    font-size: 12px;
    color: #dedede;
    display: block;
    min-width: 60px;
    line-height: 14px;
    background-color: #121212;
    border: 2px solid #343434;
    border-radius: 5px;
    text-align: center;
    user-select: none;
    padding: 4px 8px;
    position: fixed;
    z-index: 5000;
}
</style>
